import type { Dict } from "../core.js";
import type { partnerLab as vi } from "../vi/partnerLab.js";

export const partnerLab: Dict<typeof vi> = {
  title: "Tingee lab",
  subtitle: "Set « {name} » · {env} · {product}",
  back: "← Back to Integrations",
  intro:
    "Calls the REAL Tingee Open API with this credential set, before it is enabled for any app. Red buttons create / delete / switch on something real at Tingee and the bank — click twice to send.",
  loading: "Loading…",
  notFound: "Credential set not found (or not a Tingee product).",
  webhookTitle: "Webhook URL",
  webhookHelp: "Paste it into the Tingee portal › Webhook (or pass it as `webhookUrl` when creating a VA). Tingee signs with this set's secret.",
  webhookLocalWarning: "The hub has no HQ_PUBLIC_API_URL — the URL is built from the dashboard address; Tingee can only reach it if that address is public.",
  copy: "Copy",
  copied: "Copied",
  send: "Send",
  confirmSend: "Click again to SEND FOR REAL",
  sending: "Calling…",
  resultTitle: "Result",
  resultEmpty: "No call yet.",
  actionLinkHelp: "Next step is on the bank page ({key}): sign in, enter the OTP the bank sends, approve the link. Then wait for a va_status event in the webhook table below.",
  actionLinkOpen: "Open the verification link",
  rawWriteWarning: "WRITE method — really creates / deletes / switches / debits / prints at Tingee and the bank. Click twice; audited.",
  rawReadOnly: "Read-only method — changes nothing.",
  rawDocs: "Look up parameters in the SDK docs ↗",
  bankLinkWidgetTitle: "Link a bank account",
  bankLinkWidgetHelp: "Choose your bank, sign in and confirm with the OTP your bank sends.",
  bankLinkWidgetBadUrl: "Not a Tingee bank-link widget URL (only https://bank-link.tingee.vn or uat-bank-link.tingee.vn).",
  bankLinkWidgetResult: "Widget returned: {status}",
  bankLinkWidgetOpen: "Open the link widget",
  resultOk: "Success",
  resultError: "Error",
  eventsTitle: "Webhook events (refreshes every 3 seconds)",
  eventsEmpty: "No events received yet.",
  clearEvents: "Clear log",
  "col.time": "Received",
  "col.kind": "Kind",
  "col.verified": "Signature",
  "col.amount": "Amount",
  "col.account": "Account",
  "col.content": "Content",
  verifiedYes: "Valid",
  verifiedNo: "Invalid ({reason})",
  bankPick: "— choose a bank —",
  loadBanksFirst: "Click « Bank list » first to pick a bank.",
  paramsJson: "Parameters (JSON)",
  invalidJson: "Invalid JSON.",
  windowTooLong: "The time window is at most 10 days.",
  apiPicker: "Choose an API",
  rawShort: "Call any SDK method",
  badgeRead: "Read-only",
  badgeWrite: "Writes — click twice",
  needBanksBefore: "The bank list for the “Bank” field isn't loaded yet — run first:",
  "section.read": "Read (changes nothing)",
  "section.va": "Virtual accounts (VA)",
  "section.notify": "Extra notification subscription",
  "section.qr": "Dynamic QR",
  "section.link": "Bank link session",
  "section.raw": "Call an SDK method (all 72 of @tingee/sdk-node)",
  "action.banks": "Bank list",
  "action.transactions": "Transactions (≤ 10 days)",
  "action.vaPaging": "VA list",
  "action.vaCreate": "Create VA",
  "action.vaConfirm": "Confirm VA (OTP)",
  "action.vaDelete": "Delete VA",
  "action.vaDeleteConfirm": "Confirm VA deletion (OTP)",
  "action.notifyAccount": "Extra subscription (after a VA exists)",
  badTime: "Unreadable time — use yyyyMMddHHmmss (Vietnam time) or ISO.",
  "hint.notifyAccount": "An extra step AFTER the VA exists — only a few banks (e.g. ACB) use it. For notifications on the real account: « Create VA » + tick « Notify on the real account », or Scenarios › Standard VA flow with « Original account number ».",
  "hint.error1059": "This bank does not use the extra subscription step. Use « Create VA » + tick « Notify on the real account » (or Scenarios › Standard VA flow / Bank Link flow).",
  "action.notifyAccountConfirm": "Confirm notifications (OTP)",
  "action.qrCreate": "Create dynamic QR",
  "action.qrStatus": "QR status",
  "action.qrDelete": "Delete QR",
  "action.bankLinkSession": "Create link session",
  "action.call": "Call",
  "field.merchantId": "Merchant ID",
  "field.shopId": "Shop ID",
  "field.merchantAccountNumberId": "Merchant account number ID",
  "field.skipCount": "Skip",
  "field.maxResultCount": "Rows",
  "field.startTime": "From (yyyyMMddHHmmss or ISO) — empty = last 24h",
  "field.endTime": "To (yyyyMMddHHmmss or ISO) — empty = now",
  "field.vaAccountNumbers": "VA numbers (comma-separated)",
  "field.bank": "Bank",
  "field.accountType": "Account type",
  "field.accountNumber": "Account number",
  "field.accountName": "Account holder",
  "field.identity": "ID / tax code",
  "field.mobile": "Phone number",
  "field.appType": "appType",
  "field.minAmount": "Minimum amount",
  "field.maxAmount": "Maximum amount",
  "field.isNotifyAccountNumber": "Notify on the real account (no VA)",
  "field.webhookUrl": "webhookUrl",
  "field.dataAccess": "dataAccess",
  "field.transactionType": "transactionType",
  "field.confirmId": "confirmId",
  "field.otpNumber": "OTP code",
  "field.vaAccountNumber": "VA number",
  "field.qrCodeType": "QR type",
  "field.amount": "Amount",
  "field.purpose": "Description",
  "field.expireInMinute": "Expires after (minutes)",
  "field.qrAccount": "qrAccount",
  "field.billId": "billId",
  "field.type": "Session type",
  "field.redirectUrl": "redirectUrl",
  "field.metadata": "metadata",
  "field.requestId": "requestId (TPBank: required, a new one each time)",
  "field.allowedBanks": "allowedBanks (codes, comma-separated — empty = all banks)",
  "field.vaPrefix": "VA prefix (BIDV / VietinBank business)",
  "field.vaSuffix": "VA suffix (BIDV / VietinBank / OCB business)",
  "field.merchantName": "Merchant name (must start with the company legal name)",
  "field.merchantAddress": "Company address",
  "field.taxCode": "Tax code",
  "field.acbUserId": "ACB User ID (ACB household / business)",
  "field.walletId": "Wallet ID (BAOKIM)",
  "section.webhook": "Webhook (subscribe at Tingee)",
  "action.webhookSubscribe": "Subscribe webhook",
  "webhook.loading": "Loading webhook targets…",
  "webhook.target": "Receiving URL",
  "webhook.targetLab": "This set's lab",
  "webhook.targetApp": "App {app} (production, {env})",
  "webhook.urlMissing": "(hub has no HQ_PUBLIC_API_URL)",
  "webhook.scope": "Scope",
  "webhook.scope.accountNumber": "Per VA / account number",
  "webhook.scope.shop": "Per shop",
  "webhook.scope.merchant": "Per merchant",
  "webhook.targetId": "Shop / merchant ID (Tingee)",
  "webhook.active": "Active (untick = disable this URL for the scope)",
  "webhook.receiver": "The receiver verifies signatures with Client ID {hint}",
  "webhook.receiverNone": "The app has no credential set bound for this environment — the receiver has no key to verify signatures.",
  "webhook.receiverOtherSet": "The receiver uses a DIFFERENT set from the one under test — Tingee webhooks signed with this set will be rejected.",
  "webhook.authNote": "In the Tingee portal, choose API Credentials authentication with EXACTLY this set's Client ID/Secret pair — webhooks signed with another pair get 401.",
  "webhook.unmeasured": "webhook-notification exists only in the SDK, not in Tingee's docs: scope semantics and whether it overrides an older URL are UNMEASURED. There is no « all » scope (it would override webhooks of other users of the same key).",
  "webhook.alphaVaHint": "A number with letters (AGBHENO…) is a VPBank VA from bank linking — Tingee lists it as an active VA; subscribe to it like any other VA.",
  "webhook.scopesLoading": "Reading this set's merchants / shops / VAs from Tingee…",
  "webhook.scopesFailed": "Couldn't read the lists from Tingee — enter values manually. {error}",
  "webhook.scopesNeedWrite": "You need permission to edit integrations to read the merchant / shop / VA lists from Tingee.",
  "webhook.scopesWarnings": "Some reads failed: {list}",
  "webhook.scopesTruncated": "The list was cut at the page limit — filter to find, or enter manually if missing.",
  "webhook.pickShop": "Shop",
  "webhook.pickMerchant": "Merchant",
  "webhook.search": "Filter by number / name…",
  "webhook.choose": "— choose —",
  "webhook.noOptions": "Tingee returned nothing for this key.",
  "webhook.batch": "Subscribe every VA of this set ({n})",
  "webhook.batchConfirm": "Click again to SEND FOR REAL to {n} VAs",
  "webhook.batchRunning": "Sending {done}/{n}…",
  "webhook.batchOk": "accepted",
  "webhook.batchFailed": "failed {code}",
  "webhook.batchStopped": "Stopped at this VA — auth error / refused by the server, nothing more sent.",
  "webhook.batchSummary": "{ok}/{n} VAs accepted by Tingee.",
  "webhook.batchNote": "Sends one VA at a time (per VA, never « all ») to the target above (VPBank VAs with letters included); stops at the first auth error.",
  "field.method": "Method",
};
